import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.offWhite};
  padding: 2rem;
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  @media (max-width: 576px) {
    padding: 2rem 1.5rem;
    border-radius: 12px;
  }
`;

const ErrorIcon = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  background: ${props => props.isPaymentTaken ? 
    'linear-gradient(135deg, #fff3cd, #ffeaa7)' : 
    'linear-gradient(135deg, #f8d7da, #f5c6cb)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  
  @media (max-width: 576px) {
    width: 80px;
    height: 80px;
    font-size: 3rem;
    margin-bottom: 1.5rem;
  }
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-weight: 600;
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const ImportantNotice = styled.div`
  background: ${props => props.isPaymentTaken ? '#fff3cd' : '#f8d7da'};
  border: 2px solid ${props => props.isPaymentTaken ? '#ffc107' : '#dc3545'};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const NoticeTitle = styled.h3`
  font-size: 1.2rem;
  color: ${props => props.isPaymentTaken ? '#856404' : '#721c24'};
  margin: 0 0 0.75rem 0;
  font-weight: 600;
`;

const NoticeText = styled.p`
  font-size: 1rem;
  color: ${props => props.isPaymentTaken ? '#856404' : '#721c24'};
  margin: 0;
  line-height: 1.6;
`;

const ReferenceBox = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const ReferenceLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ReferenceValue = styled.div`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  font-family: monospace;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.theme.colors.accent};
  color: white;
  border-color: ${props => props.theme.colors.accent};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.colors.secondary};
  border-color: ${props => props.theme.colors.greyBackground};
  
  &:hover {
    background: ${props => props.theme.colors.greyBackground};
  }
`;

const BookingError = () => {
  const location = useLocation();
  const errorData = location.state?.errorData || {};
  
  const {
    errorType = 'GENERAL_ERROR', // 'PAYMENT_TAKEN_ORDER_FAILED', 'PAYMENT_FAILED', 'GENERAL_ERROR', 'BOOKING_METADATA_MISSING'
    errorMessage = 'An error occurred during your booking',
    bookingReference,
    orderId,
    paymentToken,
    transactionId,
    amount,
    customerEmail,
    timestamp,
    userMessage,
    systemDeprecated = false,
    diagnosticInfo
  } = errorData;
  
  const isPaymentTaken = errorType === 'PAYMENT_TAKEN_ORDER_FAILED';
  const isPaymentFailed = errorType === 'PAYMENT_FAILED';
  const isSystemDeprecated = systemDeprecated || errorType === 'BOOKING_METADATA_MISSING';
  
  const handleContactSupport = () => {
    window.location.href = 'mailto:info@belvoircastle.com?subject=Booking Error - ' + (bookingReference || orderId || 'No Reference');
  };
  
  const handleReturnHome = () => {
    window.location.href = '/';
  };
  
  const handleTryAgain = () => {
    window.location.href = '/booking-journey';
  };

  return (
    <ErrorContainer>
      <ErrorCard>
        <ErrorIcon isPaymentTaken={isPaymentTaken}>
          {isSystemDeprecated ? '🔧' : (isPaymentTaken ? '⚠️' : '❌')}
        </ErrorIcon>
        
        {isSystemDeprecated ? (
          <>
            <ErrorTitle>Booking System Issue</ErrorTitle>
            <ImportantNotice isPaymentTaken={true}>
              <NoticeTitle>⚠️ Technical Team Notified</NoticeTitle>
              <NoticeText>
                {userMessage || errorMessage}
                <br/><br/>
                <strong>Our technical team has been automatically notified</strong> via email at jhartwell@belvoircastle.com and the System Status page has been updated to reflect this issue.
              </NoticeText>
            </ImportantNotice>
            
            {orderId && (
              <ReferenceBox>
                <ReferenceLabel>Booking Reference (for support):</ReferenceLabel>
                <ReferenceValue>{orderId}</ReferenceValue>
              </ReferenceBox>
            )}
            
            <ErrorMessage>
              <strong>What you can do:</strong><br/>
              • Try selecting a different date or time slot<br/>
              • Contact our support team at <a href="mailto:info@belvoircastle.com" style={{ color: '#9d6d49', textDecoration: 'underline' }}>info@belvoircastle.com</a><br/>
              • Check our <a href="/status" style={{ color: '#9d6d49', textDecoration: 'underline' }}>System Status page</a> for updates
            </ErrorMessage>
            
            {diagnosticInfo?.pageName && (
              <ErrorMessage style={{ fontSize: '0.95rem', color: '#666', marginTop: '1rem' }}>
                Event affected: <strong>{diagnosticInfo.pageName}</strong>
              </ErrorMessage>
            )}
          </>
        ) : isPaymentTaken ? (
          <>
            <ErrorTitle>Payment Received - Booking Issue</ErrorTitle>
            <ImportantNotice isPaymentTaken={true}>
              <NoticeTitle>Important: Your Payment Was Processed</NoticeTitle>
              <NoticeText>
                We have received your payment, but encountered an issue creating your booking in our system. 
                <strong> Our team has been notified and will contact you shortly to complete your booking.</strong>
              </NoticeText>
            </ImportantNotice>
            
            {(bookingReference || orderId || transactionId) && (
              <ReferenceBox>
                <ReferenceLabel>Please keep this reference:</ReferenceLabel>
                <ReferenceValue>{bookingReference || orderId || transactionId}</ReferenceValue>
              </ReferenceBox>
            )}
            
            <ErrorMessage>
              <strong>What happens next?</strong><br/>
              We will process your booking manually and send your confirmation within 24 hours. 
              You will not be charged twice.
            </ErrorMessage>
            
            {customerEmail && (
              <ErrorMessage style={{ fontSize: '0.95rem', color: '#666' }}>
                A copy of this information has been sent to: <strong>{customerEmail}</strong>
              </ErrorMessage>
            )}
          </>
        ) : isPaymentFailed ? (
          <>
            <ErrorTitle>Payment Not Completed</ErrorTitle>
            <ImportantNotice isPaymentTaken={false}>
              <NoticeTitle>No Payment Was Taken</NoticeTitle>
              <NoticeText>
                Your payment was not processed. You have not been charged.
              </NoticeText>
            </ImportantNotice>
            
            <ErrorMessage>
              {errorMessage || 'The payment could not be completed. This sometimes happens due to payment method issues or timeout. Please try again.'}
            </ErrorMessage>
          </>
        ) : (
          <>
            <ErrorTitle>Booking Error</ErrorTitle>
            <ErrorMessage>
              {errorMessage || 'We encountered an error while processing your booking. Please try again or contact our support team.'}
            </ErrorMessage>
          </>
        )}
        
        <ActionButtons>
          {isSystemDeprecated || isPaymentTaken ? (
            <>
              <PrimaryButton onClick={handleContactSupport}>
                Contact Support
              </PrimaryButton>
              <SecondaryButton onClick={handleReturnHome}>
                Return Home
              </SecondaryButton>
            </>
          ) : (
            <>
              <PrimaryButton onClick={handleTryAgain}>
                Try Again
              </PrimaryButton>
              <SecondaryButton onClick={handleReturnHome}>
                Return Home
              </SecondaryButton>
            </>
          )}
        </ActionButtons>
        
        {(orderId || transactionId || timestamp) && (
          <div style={{ 
            marginTop: '2rem', 
            padding: '1rem', 
            background: '#f8f9fa', 
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: '#666',
            textAlign: 'left'
          }}>
            <strong>Technical Details:</strong>
            {orderId && <div>Order ID: {orderId}</div>}
            {transactionId && <div>Transaction ID: {transactionId}</div>}
            {paymentToken && <div>Payment Token: {paymentToken.substring(0, 20)}...</div>}
            {timestamp && <div>Time: {new Date(timestamp).toLocaleString()}</div>}
            {amount && <div>Amount: £{amount.toFixed(2)}</div>}
          </div>
        )}
      </ErrorCard>
    </ErrorContainer>
  );
};

export default BookingError;

